import { useState } from "react";

import { Input } from "@monorepo-apps/ui/src/components/input";
import { Label } from "@monorepo-apps/ui/src/components/label";
import { Textarea } from "@monorepo-apps/ui/src/components/textarea";

import { ComponentsMultiselect } from "@/presentation/components/products/components-multiselect";
import type { ProductKind, ProductRow } from "@/infrastructure/supabase/database.types";

export interface ProductFormValues {
  slug: string;
  name: string;
  vendor: string | null;
  kind: ProductKind;
  summary: string | null;
  homepage_url: string | null;
  notes: string | null;
  applies_to_components: string[];
}

interface ProductFormBodyProps {
  initial: ProductRow | null;
  onChange: (values: ProductFormValues) => void;
}

const DEFAULT_VALUES: ProductFormValues = {
  slug: "",
  name: "",
  vendor: null,
  kind: "off-the-shelf",
  summary: null,
  homepage_url: null,
  notes: null,
  applies_to_components: [],
};

export function ProductFormBody({ initial, onChange }: ProductFormBodyProps) {
  const [values, setValues] = useState<ProductFormValues>(
    initial
      ? {
          slug: initial.slug,
          name: initial.name,
          vendor: initial.vendor,
          kind: initial.kind,
          summary: initial.summary,
          homepage_url: initial.homepage_url,
          notes: initial.notes,
          applies_to_components: initial.applies_to_components ?? [],
        }
      : DEFAULT_VALUES
  );

  const update = (patch: Partial<ProductFormValues>) => {
    const next = { ...values, ...patch };
    setValues(next);
    onChange(next);
  };

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-3">
        <Field label="Slug">
          <Input
            value={values.slug}
            onChange={(e) =>
              update({ slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "-") })
            }
            placeholder="acme-loyalty"
            disabled={initial !== null}
          />
        </Field>
        <Field label="Kind">
          <select
            value={values.kind}
            onChange={(e) => update({ kind: e.target.value as ProductKind })}
            className="h-9 w-full rounded-md border bg-transparent px-3 text-sm"
          >
            <option value="off-the-shelf">Off-the-shelf</option>
            <option value="build-on-top">Build on top</option>
            <option value="from-scratch">From scratch</option>
          </select>
        </Field>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <Field label="Name">
          <Input
            value={values.name}
            onChange={(e) => update({ name: e.target.value })}
            placeholder="Antavo Loyalty Cloud"
          />
        </Field>
        <Field label="Vendor">
          <Input
            value={values.vendor ?? ""}
            onChange={(e) => update({ vendor: e.target.value || null })}
            placeholder="Antavo"
          />
        </Field>
      </div>

      <Field label="Homepage URL">
        <Input
          value={values.homepage_url ?? ""}
          onChange={(e) => update({ homepage_url: e.target.value || null })}
          placeholder="https://…"
          type="url"
        />
      </Field>

      <Field label="Summary">
        <Textarea
          rows={3}
          value={values.summary ?? ""}
          onChange={(e) => update({ summary: e.target.value || null })}
          placeholder="One paragraph on what this product is and where it shines."
        />
      </Field>

      <Field label="Notes (internal)">
        <Textarea
          rows={2}
          value={values.notes ?? ""}
          onChange={(e) => update({ notes: e.target.value || null })}
          placeholder="Internal context, references, contacts."
        />
      </Field>

      <Field label="Applies to components (filters product into the candidate composer)">
        <ComponentsMultiselect
          value={values.applies_to_components}
          onChange={(next) => update({ applies_to_components: next })}
        />
      </Field>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-1.5">
      <Label className="text-foreground/80 text-[11.5px] font-medium">{label}</Label>
      {children}
    </div>
  );
}
